/**
 * InvitationsListPage — route `/users/invitations`.
 *
 * Pending-invitations roster for the admin portal. Mirrors
 * `UsersListPage` for structure (header / forbidden+error+ready
 * tagged-union states / pagination) and reuses the established
 * `useInvitationsList` + `useResendInvitation` + `useRevokeInvitation`
 * hooks.
 *
 * Scope (v1):
 *  - Filter is fixed at `status=pending`. Accepted / revoked / expired
 *    invitations are NOT surfaced in v1; the BE endpoint supports
 *    them and a future story can add a status switcher.
 *  - Resend / Revoke buttons gated by their own permission slugs
 *    (identity.invitations.resend / identity.invitations.revoke).
 *  - Revoke uses native `window.confirm()` for now; `<Dialog>`
 *    is not yet in @sparta/ui-kit. Tech-debt — replace once kit
 *    primitive lands.
 */

import * as React from 'react';
import { Button } from '@sparta/ui-kit';
import {
  useInvitationsList,
  type InvitationRow,
} from '../api/use-invitations-list.js';
import {
  useResendInvitation,
  type ResendInvitationError,
} from '../api/use-resend-invitation.js';
import {
  useRevokeInvitation,
  type RevokeInvitationError,
} from '../api/use-revoke-invitation.js';
import { UsersHeader } from '../_components/UsersHeader.js';
import { RequirePermission } from '../../../shared/auth/RequirePermission.js';

const PER_PAGE = 50;

const COLUMN_ORDER = [
  'name',
  'email',
  'role',
  'expires',
  'sent',
  'actions',
] as const;

type ColumnId = (typeof COLUMN_ORDER)[number];

const COLUMN_LABELS: Record<ColumnId, string> = {
  name: 'Name',
  email: 'Email',
  role: 'Role',
  expires: 'Expires',
  sent: 'Sent',
  actions: 'Actions',
};

function formatName(firstName: string | null, lastName: string | null): string {
  const parts = [firstName, lastName].filter(
    (p): p is string => typeof p === 'string' && p.length > 0,
  );
  return parts.length > 0 ? parts.join(' ') : '—';
}

function formatDate(value: string | null): string {
  if (value === null) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

type ActionFeedback =
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string };

function resendErrorMessage(err: ResendInvitationError): string {
  switch (err.kind) {
    case 'unprocessable':
      // The BE message describes whether the invite was already accepted
      // or revoked. We surface it verbatim — it's safe (no PII) and
      // actionable for the admin.
      return `Couldn't resend invitation: ${err.reason}.`;
    case 'forbidden':
      return "You don't have permission to resend invitations.";
    case 'notFound':
      return 'Invitation not found. It may have been revoked.';
    case 'rateLimited':
      return 'Too many resends for this invitation. Try again later.';
    case 'error':
      return 'Resend failed. Try again.';
  }
}

function revokeErrorMessage(err: RevokeInvitationError): string {
  switch (err.kind) {
    case 'unprocessable':
      return `Couldn't revoke invitation: ${err.reason}.`;
    case 'forbidden':
      return "You don't have permission to revoke invitations.";
    case 'notFound':
      return 'Invitation not found.';
    case 'error':
      return 'Revoke failed. Try again.';
  }
}

export function InvitationsListPage(): React.ReactElement {
  const [page, setPage] = React.useState(1);
  const [feedback, setFeedback] = React.useState<ActionFeedback | null>(null);
  const [pendingActionRow, setPendingActionRow] = React.useState<string | null>(
    null,
  );

  const result = useInvitationsList({
    status: 'pending',
    page,
    perPage: PER_PAGE,
  });

  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();

  const onResend = async (row: InvitationRow): Promise<void> => {
    setPendingActionRow(row.id);
    setFeedback(null);
    try {
      await resend.mutateAsync({ invitationId: row.id });
      setFeedback({
        kind: 'success',
        message: `Invitation resent to ${row.email}.`,
      });
    } catch (err) {
      setFeedback({
        kind: 'error',
        message: resendErrorMessage(err as ResendInvitationError),
      });
    } finally {
      setPendingActionRow(null);
    }
  };

  const onRevoke = async (row: InvitationRow): Promise<void> => {
    // Revoke is irreversible — confirm before firing. `window.confirm`
    // is a deliberate v1 choice: @sparta/ui-kit does not ship a Dialog
    // primitive yet, and native confirm is accessible, keyboard-friendly,
    // and avoids hand-rolling modal a11y. Replace when kit primitive lands.
    const ok = window.confirm(
      `Revoke the invitation to ${row.email}? This cannot be undone — the invitee will no longer be able to accept it.`,
    );
    if (!ok) return;

    setPendingActionRow(row.id);
    setFeedback(null);
    try {
      await revoke.mutateAsync({ invitationId: row.id });
      setFeedback({
        kind: 'success',
        message: `Invitation to ${row.email} revoked.`,
      });
    } catch (err) {
      setFeedback({
        kind: 'error',
        message: revokeErrorMessage(err as RevokeInvitationError),
      });
    } finally {
      setPendingActionRow(null);
    }
  };

  if (result.status === 'loading') {
    return (
      <section
        aria-busy="true"
        aria-label="Loading invitations"
        className="pt-2 text-sm text-[var(--fg-tertiary)]"
      >
        Loading invitations…
      </section>
    );
  }

  const showCount =
    result.status === 'ready'
      ? `${result.data.length} of ${result.total}`
      : '—';
  const totalPages =
    result.status === 'ready'
      ? Math.max(1, Math.ceil(result.total / PER_PAGE))
      : 1;
  const canGoPrev = page > 1;
  const canGoNext = result.status === 'ready' && page < totalPages;

  return (
    <section
      aria-labelledby="users-page-title"
      className="flex flex-col gap-4"
    >
      <UsersHeader activeTab="invitations" count={showCount} />

      {feedback !== null ? (
        <div
          role="status"
          aria-live="polite"
          className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-3 text-sm"
          style={{
            color:
              feedback.kind === 'success'
                ? 'var(--fg-primary)'
                : 'var(--fg-danger)',
          }}
        >
          {feedback.message}
        </div>
      ) : null}

      {result.status === 'forbidden' ? (
        <div
          role="alert"
          className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-sm text-[var(--fg-secondary)]"
        >
          You don't have permission to view invitations for this
          organization. Ask an organization owner or admin if you need
          access.
        </div>
      ) : null}

      {result.status === 'error' ? (
        <div
          role="alert"
          className="flex flex-col gap-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-sm text-[var(--fg-secondary)]"
        >
          <p>We couldn't load the invitations list. The backend may be unreachable.</p>
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => result.refetch()}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {result.status === 'ready' && result.data.length === 0 ? (
        <div
          role="status"
          className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-6 text-center text-sm text-[var(--fg-secondary)]"
        >
          <p className="font-semibold text-[var(--fg-primary)]">
            No pending invitations
          </p>
          <p className="mt-1">Invitations awaiting acceptance will appear here.</p>
        </div>
      ) : null}

      {result.status === 'ready' && result.data.length > 0 ? (
        <>
          <table
            aria-label="Pending invitations"
            className="w-full text-left text-sm"
          >
            <thead>
              <tr className="border-b border-[var(--border-subtle)] text-[11px] font-bold uppercase tracking-wider text-[var(--fg-tertiary)]">
                {COLUMN_ORDER.map((column) => (
                  <th key={column} scope="col" className="py-3 pr-4">
                    {COLUMN_LABELS[column]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.data.map((row) => {
                const isBusy = pendingActionRow === row.id;
                return (
                  <tr
                    key={row.id}
                    className="border-b border-[var(--border-subtle)]"
                  >
                    <td className="py-3 pr-4 text-[var(--fg-primary)]">
                      {formatName(row.firstName, row.lastName)}
                    </td>
                    <td className="py-3 pr-4 text-[var(--fg-secondary)]">
                      <span className="truncate">{row.email}</span>
                    </td>
                    <td className="py-3 pr-4 text-[var(--fg-secondary)]">
                      {row.role.displayName}
                    </td>
                    <td className="py-3 pr-4 text-[var(--fg-secondary)]">
                      {formatDate(row.expiresAt)}
                    </td>
                    <td className="py-3 pr-4 text-[var(--fg-secondary)]">
                      {formatDate(row.createdAt)}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <RequirePermission slug="identity.invitations.resend">
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={isBusy}
                            aria-busy={isBusy}
                            onClick={() => {
                              void onResend(row);
                            }}
                          >
                            Resend
                          </Button>
                        </RequirePermission>
                        <RequirePermission slug="identity.invitations.revoke">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isBusy}
                            aria-busy={isBusy}
                            onClick={() => {
                              void onRevoke(row);
                            }}
                          >
                            Revoke
                          </Button>
                        </RequirePermission>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <nav
            aria-label="Pagination"
            className="flex items-center justify-between gap-3 pt-2 text-sm text-[var(--fg-secondary)]"
          >
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                aria-label="Previous page"
                disabled={!canGoPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                aria-label="Next page"
                disabled={!canGoNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </nav>
        </>
      ) : null}
    </section>
  );
}
